import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import Card from './Card';
import SearchableDropdown from './SearchableDropdown';
import SearchableInput from './SearchableInput';
import YearSelector from './YearSelector';
import {
  MALAYSIAN_EXAMS,
  MALAYSIAN_SUBJECTS,
  EXAM_GRADES,
} from '../../constants/dropdownOptions';
import type { ExamResult } from '../../types';
import { useOptionalI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';

interface PastPublicExamResultsProps {
  examResults: ExamResult[];
  onAddExamResult: () => void;
  onRemoveExamResult: (id: string) => void;
  onUpdateExamResult: (id: string, updates: Partial<ExamResult>) => void;
  onUpdateSubjectResult: (
    examId: string,
    subject: string,
    grade: string,
    isCompulsory: boolean
  ) => void;
  onRemoveSubjectResult: (examId: string, subject: string) => void;
}

const PastPublicExamResults: React.FC<PastPublicExamResultsProps> = ({
  examResults,
  onAddExamResult,
  onRemoveExamResult,
  onUpdateExamResult,
  onUpdateSubjectResult,
  onRemoveSubjectResult,
}) => {
  const { locale } = useOptionalI18n();
  const [expandedExams, setExpandedExams] = useState<Set<string>>(new Set());

  const toggleExamExpanded = (examId: string) => {
    const newExpanded = new Set(expandedExams);
    if (newExpanded.has(examId)) {
      newExpanded.delete(examId);
    } else {
      newExpanded.add(examId);
    }
    setExpandedExams(newExpanded);
  };

  const addSubjectToExam = (
    examId: string,
    subject: string,
    isCompulsory: boolean
  ) => {
    const exam = examResults.find((e) => e.id === examId);
    if (exam) {
      const existingResult = exam.results.find((r) => r.subject === subject);
      if (!existingResult) {
        const newResults = [
          ...exam.results,
          { subject, grade: '', isCompulsory },
        ];
        onUpdateExamResult(examId, { results: newResults });
      }
    }
  };

  const currentYear = new Date().getFullYear();

  if (examResults.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
            <h3 className="text-xl font-semibold">{localizeText(locale, 'Past Public Exam Results')}</h3>
            <span className="text-sm text-gray-500"> {localizeText(locale, "(It's required, but you can skip it for now)")}</span>
          </div>
          <button
            type="button"
            onClick={onAddExamResult}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {localizeText(locale, 'Add Exam Result')}
          </button>
        </div>

        <div className="p-6 text-center border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-600">{localizeText(locale, 'No exam results added yet')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
          <h3 className="text-xl font-semibold">{localizeText(locale, 'Past Public Exam Results')}</h3>
          <span className="text-sm text-gray-500">{localizeText(locale, 'Required, can add later')}</span>
        </div>
        <button
          type="button"
          onClick={onAddExamResult}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {localizeText(locale, 'Add Exam Result')}
        </button>
      </div>

      <div className="space-y-3">
        {[...examResults].reverse().map((exam) => {
          const isExpanded = expandedExams.has(exam.id);

          return (
            <Card key={exam.id} variant="outline" padding="md">
              {/* Layer 1: Exam Type + Year */}
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => toggleExamExpanded(exam.id)}
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center gap-3">
                    <h4 className="text-lg font-semibold text-gray-800">
                      {exam.examType ? localizeText(locale, exam.examType) : localizeText(locale, 'Select Exam Type')}
                    </h4>
                    {exam.year && (
                      <span className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-600 font-medium">
                        {exam.year}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {examResults.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveExamResult(exam.id);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                  <button type="button" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Layer 2: Expandable Content */}
              {isExpanded && (
                <div className="mt-6 space-y-6 border-t pt-6">
                  {/* Edit Exam Type and Year */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {localizeText(locale, 'Exam Type')} <span className="text-red-500">*</span>
                      </label>
                      <SearchableDropdown
                        options={MALAYSIAN_EXAMS}
                        value={exam.examType}
                        onChange={(value) => {
                          onUpdateExamResult(exam.id, {
                            examType: String(value),
                            results: [],
                          });
                        }}
                        placeholder={localizeText(locale, 'Select exam type')}
                        searchable={false}
                      />
                    </div>

                    <div>
                      <YearSelector
                        label={<>{localizeText(locale, 'Year')} <span className="text-red-500">*</span></>}
                        min={1970}
                        max={currentYear}
                        value={exam.year}
                        onChange={(year) =>
                          onUpdateExamResult(exam.id, {
                            year,
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Subject Results Section */}
                  {exam.examType && (
                    <div className="space-y-4 border-t pt-4">
                      {/* Add Subject from MALAYSIAN_SUBJECTS */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          {localizeText(locale, 'Add Subjects')}
                        </label>
                        <SearchableInput
                          options={MALAYSIAN_SUBJECTS.filter(
                            (s) => !exam.results.find((r) => r.subject === s)
                          )}
                          value=""
                          onChange={(subject) => {
                            if (subject) {
                              addSubjectToExam(exam.id, subject, false);
                            }
                          }}
                          placeholder={localizeText(locale, 'Search and select a subject...')}
                          confirmCustomInput={true}
                        />
                      </div>

                      {/* Grade Selection for All Added Subjects */}
                      {exam.results.length > 0 && (
                        <div className="space-y-3 border-t pt-4">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            {localizeText(locale, 'Subject Grades')}
                          </label>
                          {exam.results.map((result) => {
                            const isCustomSubject = !MALAYSIAN_SUBJECTS.includes(result.subject);

                            return (
                              <div
                                key={result.subject}
                                className="flex items-center gap-3"
                              >
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-700">
                                    {localizeText(locale, result.subject)}
                                  </span>
                                </div>
                                <div className="flex gap-2">
                                  {isCustomSubject ? (
                                    <input
                                      type="text"
                                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                      placeholder={localizeText(locale, 'Enter grade (e.g. A+, 90, Distinction)')}
                                      value={result.grade}
                                      onChange={(e) =>
                                        onUpdateSubjectResult(
                                          exam.id,
                                          result.subject,
                                          e.target.value,
                                          result.isCompulsory
                                        )
                                      }
                                    />
                                  ) : (
                                    <SearchableDropdown
                                      options={EXAM_GRADES}
                                      value={result.grade}
                                      onChange={(grade) =>
                                        onUpdateSubjectResult(
                                          exam.id,
                                          result.subject,
                                          String(grade),
                                          result.isCompulsory
                                        )
                                      }
                                      placeholder={localizeText(locale, 'Select Grade')}
                                      searchable={true}
                                    />
                                  )}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      onRemoveSubjectResult(
                                        exam.id,
                                        result.subject
                                      )
                                    }
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PastPublicExamResults;
